/**
 * Expanded body of a Google carousel AD row: its cards, each with its images.
 * Stands in for the AD-parented asset strip, which finds nothing for a carousel
 * because carousel images hang off their card rows.
 */
import React from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { CarouselCardBlock } from './CarouselCardBlock';
import { useCarouselCardTree } from './useCarouselCardTree';

interface CarouselCardListProps {
  adSourceEntityId: string | undefined;
  campaignId: string;
}

const NO_IMAGES: PublisherEntityDto[] = [];

export const CarouselCardList: React.FC<CarouselCardListProps> = ({
  adSourceEntityId,
  campaignId,
}) => {
  const { t } = useTranslation();
  const { cards, imagesByCard, isError, isLoading } = useCarouselCardTree({ adSourceEntityId, campaignId });

  if (isLoading) {
    return (
      <Row alignmentHorizontal="center" data-testid="ad-carousel-cards-loading">
        <MeridianLoader size="small" />
      </Row>
    );
  }

  if (isError) {
    return (
      <Alert data-testid="ad-carousel-cards-error" size="small" type="error">
        {t('ads_manager_carousel_cards_error')}
      </Alert>
    );
  }

  if (cards.length === 0) {
    return (
      <Text color="secondary" data-testid="ad-carousel-cards-empty">
        {t('ads_manager_carousel_no_cards')}
      </Text>
    );
  }

  return (
    <Column data-testid="ad-carousel-cards" spacing="200">
      <Text color="secondary" type="b200">{t('ads_manager_carousel_cards')} ({cards.length})</Text>
      {cards.map(card => (
        <CarouselCardBlock
          card={card}
          images={imagesByCard.get(card.sourceEntityId ?? '') ?? NO_IMAGES}
          key={card._id}
        />
      ))}
    </Column>
  );
};
